import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Chip, TextField } from '@mui/material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import type { ContactTagRow } from './LeadContactTags';

/** Query suffix for GET /conversations (OR: contact has any of the tags). */
export function inboxTagFilterQuery(tagIds: string[]): string {
  if (!tagIds.length) return '';
  return `&tag_id=${tagIds.map(encodeURIComponent).join(',')}`;
}

interface InboxConversationTagFilterProps {
  selectedTagIds: string[];
  onChange: (tagIds: string[]) => void;
}

export default function InboxConversationTagFilter({
  selectedTagIds,
  onChange
}: InboxConversationTagFilterProps) {
  const [options, setOptions] = useState<ContactTagRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${getApiUrl()}/contact-tags`)
      .then((res) => {
        if (!cancelled && res.data?.success) {
          setOptions(res.data.data || []);
        }
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => options.filter((o) => selectedTagIds.includes(o.id)),
    [options, selectedTagIds]
  );

  return (
    <Box>
      <Autocomplete
        multiple
        options={options}
        value={value}
        onChange={(_, v) => onChange(v.map((t) => t.id))}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        size="small"
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={option.name}
              size="small"
              sx={{
                borderLeft: '3px solid',
                borderColor: option.color || '#9e9e9e',
                height: 24,
                '& .MuiChip-label': { fontSize: '0.72rem' }
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label="Filtrar por tag" placeholder="Qualquer uma…" />
        )}
      />
    </Box>
  );
}
